import type { TFunction } from "i18next";
import {
  Currency,
  NeedStatus,
  ProcurementPhaseDto,
  type PublicEventStatusDto,
  type NeedDto,
  type PersonDto,
  type PlannedProcurementDto,
} from "../../autogen/bff-api";
import { osloDate } from "../../common/download/listExport";
import { needStatusLabel } from "../../common/needs/useNeedStatusLabel";
import { displayPersonName } from "../../common/persons/displayPersonName";
import { publicStatusLabel } from "../../common/sourcing-events/EventStatusTag";
import { urls } from "../../urls";
import { procedureText, regulationText } from "../sourcing-events/procurementClassificationText";
import type { PeriodSet } from "./OverviewPeriodChoice";
import type { PeriodRange } from "./procurementFigures";

export type PlanValueSource = "procurer" | "estimated";

export type PlanPerson = { id: string; name: string; email: string };

export type PlanEntry = {
  key: string;
  id: string;
  kind: "procurement" | "need";
  title: string;
  phase: string;
  status: { kind: "procurement"; status: PublicEventStatusDto } | { kind: "need"; status: NeedStatus } | null;
  procurementPhase: ProcurementPhaseDto | null;
  regulation: string | null;
  procedure: string | null;
  responsible: PlanPerson | null;
  department: { id: string; name: string } | null;
  value: number | null;
  currency: Currency;
  valueSource: PlanValueSource | null;
  valueUnknown: boolean;
  negotiating: boolean;
  deadline: string | null;
  desiredDelivery: string | null;
  month: string | null;
  day: string | null;
  link: string;
};

export const procurementEntry = (planned: PlannedProcurementDto, t: TFunction, publishedLabel: string): PlanEntry => {
  const { event } = planned;
  const day = event.deadline ? osloDate(event.deadline) : null;
  return {
    key: `procurement-${event.id}`,
    id: event.id,
    kind: "procurement",
    title: event.title || t("Untitled"),
    phase: event.publicStatus ? publicStatusLabel(event.publicStatus, t, publishedLabel) : "",
    status: event.publicStatus ? { kind: "procurement", status: event.publicStatus } : null,
    procurementPhase: planned.phase,
    regulation: regulationText(event, t),
    procedure: procedureText(event, t),
    responsible: person(event.responsible),
    department: planned.department ? { id: planned.department.id, name: planned.department.name } : null,
    value: planned.estimatedValue?.value ?? null,
    currency: planned.estimatedValue?.currency ?? Currency.Nok,
    valueSource: null,
    valueUnknown: false,
    negotiating: event.negotiationRound != null,
    deadline: event.deadline ?? null,
    desiredDelivery: planned.desiredDelivery ?? null,
    month: day?.slice(0, 7) ?? null,
    day,
    link: urls.app.sourcingEvents.view.go(event.id),
  };
};

export const needEntry = (need: NeedDto, t: TFunction): PlanEntry => {
  const procurerValue = need.procurerAssessment?.value ?? null;
  const estimatedValue = need.estimatedValue?.value ?? null;
  const plannedStart = need.plannedStart
    ? `${need.plannedStart.year}-${String(need.plannedStart.month).padStart(2, "0")}`
    : null;
  return {
    key: `need-${need.id}`,
    id: need.id,
    kind: "need",
    title: need.title || t("Untitled"),
    phase: needStatusLabel(need.status, t),
    status: { kind: "need", status: need.status },
    procurementPhase: null,
    regulation: null,
    procedure: null,
    responsible: need.responsibleBuyer ? person(need.responsibleBuyer) : null,
    department: need.department ? { id: need.department.id, name: need.department.name } : null,
    value: procurerValue ?? estimatedValue,
    currency: need.estimatedValue?.currency ?? Currency.Nok,
    valueSource: procurerValue != null ? "procurer" : estimatedValue != null ? "estimated" : null,
    valueUnknown: procurerValue == null && estimatedValue == null && need.estimatedValue?.unknown === true,
    negotiating: false,
    deadline: null,
    desiredDelivery: need.deliveryDate ?? null,
    month: plannedStart ?? need.deliveryDate?.slice(0, 7) ?? null,
    day: plannedStart ? null : (need.deliveryDate ?? null),
    link: urls.app.needs.view.go(need.id),
  };
};

const person = (dto: PersonDto) => ({
  id: dto.id,
  name: displayPersonName(dto).trim(),
  email: dto.email,
});

export type PlanPeriod = "all" | "thisQuarter" | "nextQuarter" | "thisYear" | "nextYear" | "custom";

const PLAN_PERIODS: PlanPeriod[] = ["all", "thisQuarter", "nextQuarter", "thisYear", "nextYear", "custom"];

const planPeriodLabel = (t: TFunction, period: PlanPeriod): string =>
  ({
    all: t("All"),
    thisQuarter: t("This quarter"),
    nextQuarter: t("Next quarter"),
    thisYear: t("This year"),
    nextYear: t("Next year"),
    custom: t("Custom"),
  })[period];

export const planPeriodRange = (
  period: PlanPeriod,
  today: Date,
  custom: { from: string | null; to: string | null },
): PeriodRange | null => {
  const year = today.getFullYear();
  const quarterStart = today.getMonth() - (today.getMonth() % 3);
  switch (period) {
    case "all":
      return null;
    case "thisQuarter":
      return monthSpan(year, quarterStart, 3);
    case "nextQuarter":
      return monthSpan(year, quarterStart + 3, 3);
    case "thisYear":
      return monthSpan(year, 0, 12);
    case "nextYear":
      return monthSpan(year + 1, 0, 12);
    case "custom": {
      const { from, to } = custom;
      return from && to && ISO_DATE.test(from) && ISO_DATE.test(to) && from <= to ? { from, to } : null;
    }
  }
};

export const PLAN_PERIOD_SET: PeriodSet<PlanPeriod> = {
  periods: PLAN_PERIODS,
  parse: (value) => PLAN_PERIODS.find((period) => period === value) ?? "all",
  label: planPeriodLabel,
  range: planPeriodRange,
  unbounded: "all",
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const monthSpan = (year: number, firstMonth: number, months: number): PeriodRange => ({
  from: isoDate(new Date(year, firstMonth, 1)),
  to: isoDate(new Date(year, firstMonth + months, 0)),
});

const isoDate = (date: Date): string =>
  [date.getFullYear(), date.getMonth() + 1, date.getDate()].map((part) => String(part).padStart(2, "0")).join("-");

export type PlanFilter = {
  range: PeriodRange | null;
  departmentId: string | null;
  responsibleId: string | null;
};

export const filterPlan = (entries: PlanEntry[], filter: PlanFilter): PlanEntry[] =>
  entries.filter(
    (entry) =>
      inPeriod(entry, filter.range) &&
      matches(entry.department?.id, filter.departmentId) &&
      matches(entry.responsible?.id, filter.responsibleId),
  );

const matches = (value: string | undefined, wanted: string | null): boolean => wanted === null || value === wanted;

const inPeriod = (entry: PlanEntry, range: PeriodRange | null): boolean => {
  if (range === null || entry.month === null) return true;
  if (entry.day) return entry.day >= range.from && entry.day <= range.to;
  return entry.month >= range.from.slice(0, 7) && entry.month <= range.to.slice(0, 7);
};

export type PlanMonth = {
  month: string | null;
  entries: PlanEntry[];
  sum: number;
};

export const groupByMonth = (entries: PlanEntry[]): PlanMonth[] => {
  const byMonth = new Map<string | null, PlanEntry[]>();
  [...entries].sort(byDayThenTitle).forEach((entry) => {
    byMonth.set(entry.month, [...(byMonth.get(entry.month) ?? []), entry]);
  });
  return [...byMonth.entries()]
    .sort(([a], [b]) => (a === null ? 1 : b === null ? -1 : a.localeCompare(b)))
    .map(([month, inMonth]) => ({
      month,
      entries: inMonth,
      sum: planTotals(inMonth).sum,
    }));
};

const byDayThenTitle = (a: PlanEntry, b: PlanEntry): number =>
  (a.day ?? `${a.month}-00`).localeCompare(b.day ?? `${b.month}-00`) || a.title.localeCompare(b.title, "nb");

export type PlanTotals = {
  sum: number;
  withoutValue: number;
  otherCurrency: number;
};

export const planTotals = (entries: PlanEntry[]): PlanTotals =>
  entries.reduce<PlanTotals>(
    (totals, entry) =>
      entry.value === null
        ? { ...totals, withoutValue: totals.withoutValue + 1 }
        : entry.currency !== Currency.Nok
          ? { ...totals, otherCurrency: totals.otherCurrency + 1 }
          : { ...totals, sum: totals.sum + entry.value },
    { sum: 0, withoutValue: 0, otherCurrency: 0 },
  );

export const monthHeading = (month: string | null, t: TFunction, language: string): string => {
  if (month === null) return t("No date");
  const name = monthName(month, language);
  return name.charAt(0).toUpperCase() + name.slice(1);
};

export const plannedStartOf = (entry: PlanEntry): string | null =>
  entry.kind === "need" && entry.month && !entry.day ? entry.month : null;

export const monthName = (month: string, language: string): string => {
  const [year, monthNumber] = month.split("-").map(Number);
  return new Date(year, monthNumber - 1, 1).toLocaleDateString(language === "en" ? "en-GB" : "nb-NO", {
    month: "long",
    year: "numeric",
  });
};

export type BoardColumn = "withProcurer" | "planned" | "draft" | "published" | "evaluation";

export const BOARD_COLUMNS: BoardColumn[] = ["withProcurer", "planned", "draft", "published", "evaluation"];

const PHASE_COLUMN: Record<ProcurementPhaseDto, BoardColumn | null> = {
  [ProcurementPhaseDto.InProgress]: "draft",
  [ProcurementPhaseDto.Announced]: "published",
  [ProcurementPhaseDto.Evaluation]: "evaluation",
  [ProcurementPhaseDto.Award]: "evaluation",
  [ProcurementPhaseDto.Completed]: null,
  [ProcurementPhaseDto.Cancelled]: null,
};

export const boardColumn = (entry: PlanEntry): BoardColumn | null => {
  if (entry.kind === "need") return entry.status?.kind === "need" && entry.status.status === NeedStatus.Planned ? "planned" : "withProcurer";
  return entry.procurementPhase ? PHASE_COLUMN[entry.procurementPhase] : null;
};

export const byBoardColumn = (entries: PlanEntry[]): Record<BoardColumn, PlanEntry[]> => {
  const ordered = groupByMonth(entries).flatMap((month) => month.entries);
  return Object.fromEntries(
    BOARD_COLUMNS.map((column) => [column, ordered.filter((entry) => boardColumn(entry) === column)]),
  ) as Record<BoardColumn, PlanEntry[]>;
};

export const procurementPlanFileName = (now: Date): string => `Anskaffelsesplan-${now.toLocaleDateString("sv-SE")}`;
